/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import React, { useEffect, lazy, Suspense } from 'react'

import { ErrorBoundary } from 'react-error-boundary'
import { HashRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom'
import { Hub } from 'aws-amplify/utils'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { logoutUser, useUser } from '../hooks/useUser'
import { SpeakersProvider } from '../hooks/useSpeakers'
import { SessionsProvider, useSessions } from '../hooks/useSessions'
import { GondolasProvider } from '../hooks/useGondolas'
import ErrorFallback from './ErrorFallback'
import Header from './sections/Header/Header'
import Footer from './sections/Footer/Footer'
import ChatWidget from './Chat/Chat'
import { Home } from './Home'
import { findBySlug } from '../utils/slugify'

// Lazy-loaded route components (code splitting)
const Privacy = lazy(() => import('./Legal/Privacy').then(m => ({ default: m.Privacy })))
const Imprint = lazy(() => import('./Legal/Imprint').then(m => ({ default: m.Imprint })))
const RegistrationForm = lazy(() => import('./Auth/RegistrationForm').then(m => ({ default: m.RegistrationForm })))
const Login = lazy(() => import('./User/Login').then(m => ({ default: m.Login })))
const ForgotPassword = lazy(() => import('./User/ForgotPassword').then(m => ({ default: m.ForgotPassword })))
const Success = lazy(() => import('./User/Success').then(m => ({ default: m.Success })))
const AuthCallback = lazy(() => import('./User/AuthCallback').then(m => ({ default: m.AuthCallback })))
const Schedule = lazy(() => import('./Schedule').then(m => ({ default: m.Schedule })))
const Speaker = lazy(() => import('./Speaker').then(m => ({ default: m.Speaker })))
const SpeakerPopup = lazy(() => import('./SpeakerPopup').then(m => ({ default: m.SpeakerPopup })))
const SessionPopup = lazy(() => import('./SessionPopup').then(m => ({ default: m.SessionPopup })))
const GondolaDetail = lazy(() => import('./Booking/GondolaDetail').then(m => ({ default: m.GondolaDetail })))
const GondolaList = lazy(() => import('./Booking/GondolaList').then(m => ({ default: m.GondolaList })))
const ProtectedRoute = lazy(() => import('./Auth/ProtectedRoute').then(m => ({ default: m.ProtectedRoute })))
const HostCockpit = lazy(() => import('./Host/HostCockpit').then(m => ({ default: m.HostCockpit })))
const CheckInPage = lazy(() => import('./Host/CheckInPage').then(m => ({ default: m.CheckInPage })))
const AdminDashboard = lazy(() => import('./Admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const ClaimFlow = lazy(() => import('./Booking/ClaimFlow').then(m => ({ default: m.ClaimFlow })))
const BookingConfirmation = lazy(() => import('./Booking/BookingConfirmation').then(m => ({ default: m.BookingConfirmation })))
const BookingFlow = lazy(() => import('./Booking/BookingFlow').then(m => ({ default: m.BookingFlow })))
const ConsentProfile = lazy(() => import('./User/ConsentProfile').then(m => ({ default: m.ConsentProfile })))
const ConsentGate = lazy(() => import('./Auth/ConsentGate').then(m => ({ default: m.ConsentGate })))
const DesignSystemTest = lazy(() => import('./DesignSystemTest'))
const PopupTestPage = lazy(() => import('./PopupTestPage'))
const HubSpotTestPage = lazy(() => import('./HubSpotTestPage'))
const Gallery = lazy(() => import('./Gallery/Gallery'))
const ShortcutsPage = lazy(() => import('./ShortcutsPage'))

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5min fresh
      cacheTime: 30 * 60 * 1000, // 30min in cache
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

const SkeletonAppShell = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="bg-white border-b h-16 px-4 flex items-center justify-between">
      <div className="animate-pulse bg-gray-200 h-8 w-32 rounded" />
      <div className="animate-pulse bg-gray-200 h-10 w-10 rounded-full" />
    </div>
    <div className="container mx-auto px-4 py-8">
      <div className="animate-pulse space-y-4">
        <div className="bg-gray-200 h-12 w-2/3 rounded" />
        <div className="bg-gray-200 h-64 rounded-lg" />
      </div>
    </div>
  </div>
)

// Stable fallback and error handler references (avoid inline arrow functions)
const PageFallback = (props) => <ErrorFallback {...props} variant="page" />
const SectionFallback = (props) => <ErrorFallback {...props} variant="section" />
const onGlobalError = (error, info) => console.error('[ErrorBoundary:global]', error, info)
const onBookingError = (error, info) => console.error('[ErrorBoundary:booking]', error, info)
const onHostError = (error, info) => console.error('[ErrorBoundary:host]', error, info)
const onAdminError = (error, info) => console.error('[ErrorBoundary:admin]', error, info)

function SessionPopupRoute() {
  const { sessionId } = useParams()
  const { sessions } = useSessions()
  const session = findBySlug(sessions, sessionId)
  return session ? <SessionPopup session={session} /> : null
}

function AppContent(props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { loading, authState } = useUser()

  // Redirect pending users (Social Login without consent) to /consent
  useEffect(() => {
    if (loading || authState !== 'pending') return
    const CONSENT_EXEMPT = ['/consent', '/auth/callback', '/register']
    if (!CONSENT_EXEMPT.includes(location.pathname)) {
      navigate('/consent', { replace: true })
    }
  }, [authState, loading, location.pathname, navigate])

  useEffect(() => {
    // Listen for OAuth events
    const hubListener = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        // Don't navigate here — useUser will resolve authState and the
        // consent-redirect effect above handles routing (pending → /consent,
        // loggedIn → schedule). Navigating here races with that effect and
        // causes screen flashing.
      }
      
      if (payload.event === 'signInWithRedirect_failure') {
        console.error('OAuth failed:', payload.data)
        navigate('/login')
      }
    })

    return hubListener
  }, [navigate])

  // Logout function
  const signOutRedirect = () => {
    logoutUser()
    window.location.href = '/'
  }

  const isDevTestPage = process.env.NODE_ENV === 'development' && [
    '/design-system-test', '/popup-test', '/hubspot-test',
  ].includes(location.pathname)

  if (loading && !isDevTestPage) {
    return <SkeletonAppShell />
  }

  return (
    <ErrorBoundary
      FallbackComponent={PageFallback}
      onError={onGlobalError}
      resetKeys={[location.pathname, location.key]}
    >
      <QueryClientProvider client={queryClient}>
        <SpeakersProvider>
          <SessionsProvider>
            <GondolasProvider>
              <div className="min-h-screen flex flex-col">
                  <Header signOutRedirect={signOutRedirect} />
                  <main className="flex-1 overflow-visible pb-20 px-4 md:px-8 lg:px-12 max-w-[1400px] mx-auto w-full">
                    <Suspense fallback={<SkeletonAppShell />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/register" element={<RegistrationForm />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/success" element={<Success />} />
                      <Route path="/speaker" element={<Speaker />} />
                      <Route
                        path="/speaker/:speakerId"
                        element={
                          <>
                            <Speaker />
                            <SpeakerPopup />
                          </>
                        }
                      />
                      <Route
                        path="/speaker/session/:sessionId"
                        element={
                          <>
                            <Speaker />
                            <SessionPopupRoute />
                          </>
                        }
                      />
                      <Route path="/datenschutz" element={<Privacy />} />
                      <Route path="/impressum" element={<Imprint />} />
                      {process.env.NODE_ENV === 'development' && (
                        <>
                          <Route path="/design-system-test" element={<DesignSystemTest />} />
                          <Route path="/popup-test" element={<PopupTestPage />} />
                          <Route path="/hubspot-test" element={<HubSpotTestPage />} />
                        </>
                      )}
                      <Route path="/gondolas" element={<GondolaList />} />
                      <Route path="/gondola/:gondolaId" element={<GondolaDetail />} />
                      <Route path="/gondola/:gondolaId/book" element={
                        <ErrorBoundary
                          FallbackComponent={SectionFallback}
                          onError={onBookingError}
                          resetKeys={[location.pathname, location.key]}
                        >
                          <BookingFlow />
                        </ErrorBoundary>
                      } />
                      <Route
                        path="/host"
                        element={
                          <ErrorBoundary
                            FallbackComponent={PageFallback}
                            onError={onHostError}
                            resetKeys={[location.pathname, location.key]}
                          >
                            <ProtectedRoute allowedRoles={['speaker', 'host', 'admin']}>
                              <HostCockpit />
                            </ProtectedRoute>
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/host/checkin"
                        element={
                          <ErrorBoundary
                            FallbackComponent={PageFallback}
                            onError={onHostError}
                            resetKeys={[location.pathname, location.key]}
                          >
                            <ProtectedRoute allowedRoles={['speaker', 'host', 'admin']}>
                              <CheckInPage />
                            </ProtectedRoute>
                          </ErrorBoundary>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ErrorBoundary
                            FallbackComponent={PageFallback}
                            onError={onAdminError}
                            resetKeys={[location.pathname, location.key]}
                          >
                            <ProtectedRoute allowedRoles={['admin']}>
                              <AdminDashboard />
                            </ProtectedRoute>
                          </ErrorBoundary>
                        }
                      />
                      <Route path="/claim/:token" element={
                        <ErrorBoundary
                          FallbackComponent={SectionFallback}
                          onError={onBookingError}
                          resetKeys={[location.pathname, location.key]}
                        >
                          <ClaimFlow />
                        </ErrorBoundary>
                      } />
                      <Route path="/booking/:bookingId" element={
                        <ErrorBoundary
                          FallbackComponent={SectionFallback}
                          onError={onBookingError}
                          resetKeys={[location.pathname, location.key]}
                        >
                          <BookingConfirmation />
                        </ErrorBoundary>
                      } />
                      <Route path="/profil/datenschutz" element={<ConsentProfile />} />
                      <Route path="/consent" element={<ConsentGate />} />
                      <Route path="/galerie" element={<Gallery />} />
                      <Route path="/entdecken" element={<ShortcutsPage />} />
                      <Route path="/:scheduleId" element={<Schedule />} />
                      <Route path="/:scheduleId/session/:sessionId" element={<Schedule />} />
                      <Route
                        path="/:scheduleId/:speakerId"
                        element={
                          <>
                            <Schedule />
                            <SpeakerPopup />
                          </>
                        }
                      />
                    </Routes>
                    </Suspense>
                  </main>
                  <Footer />
                  <ChatWidget />
              </div>
            </GondolasProvider>
          </SessionsProvider>
        </SpeakersProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

function App(props) {
  // use exc runtime event handlers
  // respond to configuration change events (e.g. user switches org)
  props.runtime.on('configuration', ({ imsOrg, imsToken, locale }) => {
    // Configuration changed
  })
  // respond to history change events
  props.runtime.on('history', ({ type, path }) => {
    // History changed
  })

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent {...props} />
    </Router>
  )
}

export default App
